import {
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
  computed,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DecimalInputDirective } from '@fmnts/components/numeric';
import { FmntsRadioGroup, RadioCardComponent } from '@fmnts/components/radio';
import {
  Option,
  isNil,
  isNotNull,
  matchOption,
  optionHasValue,
} from '@fmnts/core';
import * as Decimal from '@fmnts/core/decimal';
import { LetDirective } from '@ngrx/component';
import {
  debounceTime,
  filter,
  map,
  skip,
  startWith,
  withLatestFrom,
} from 'rxjs';
import { SharedDeclarativeCampaignFormsModule } from '../../shared-declarative-next-forms.module';
import {
  AbstractAmountFieldComponent,
  AmountFieldValue,
  AmountFieldValueEquivalence,
  AmountFieldValueFromUnknown,
  AmountProposalItem,
} from './abstract-donation-amount-field';

/**
 * Event object emitted by `DonationAmountFieldComponent`
 * when the amount changed.
 */
class AmountChange {
  constructor(
    /** The new amount. */
    public amount: AmountFieldValue,
    /** Whether the change was a result of a user interaction. */
    public isUserInput: boolean,
    /**
     * The amount proposal that was selected.
     * Might be `undefined` if a custom amount was entered or
     * when the amount was changed programmatically.
     */
    public proposal?: AmountProposalItem,
  ) {}
}

function findWithValue(needle: Decimal.Decimal) {
  return ({ value }: AmountProposalItem) =>
    !isNil(value) && needle.equals(value);
}

/**
 * Field component for displaying amount proposals and
 * an input field for entering a custom amount.
 */
@Component({
  selector: 'next-donation-amount-field',
  templateUrl: './donation-amount-field.component.html',
  styleUrl: './donation-amount-field.component.scss',
  standalone: true,
  imports: [
    DecimalInputDirective,
    FmntsRadioGroup,
    LetDirective,
    RadioCardComponent,
    SharedDeclarativeCampaignFormsModule,
  ],
  encapsulation: ViewEncapsulation.None,
})
export class DonationAmountFieldComponent
  extends AbstractAmountFieldComponent
  implements OnInit
{
  @HostBinding('class') componentClass =
    'form-field form-field__donation-amount';

  /**
   * Emitted when the amount changes by entering a custom amount or
   * by selecting a proposal
   */
  @Output() readonly amountChange = new EventEmitter<AmountChange>();

  @ViewChild('customInput')
  protected customInputRef!: ElementRef<HTMLInputElement>;

  /**
   * Value of the donation amount field.
   */
  get value(): AmountFieldValue {
    return this._value();
  }
  set value(value: AmountFieldValue) {
    if (AmountFieldValueEquivalence(value, this._value())) {
      return;
    }
    this._value.set(value);
    this._onChange(this._value());
  }

  /** Value of the selected radio group item. */
  protected get selectedOption() {
    return this._selectedOption();
  }
  protected set selectedOption(
    value: Option<AmountProposalItem['value'] | symbol>,
  ) {
    this._selectedOption.set(value);
  }
  private readonly _selectedOption =
    signal<Option<AmountProposalItem['value'] | symbol>>(null);

  /** `true` if a proposal is selected. */
  private readonly proposalSelected = computed(
    () =>
      optionHasValue(this._selectedOption()) && !this._customOptionSelected(),
  );

  /** Custom amount entered by the user. */
  protected readonly _customValue = signal<AmountFieldValue>(null, {
    equal: AmountFieldValueEquivalence,
  });

  /** `true`, if the current value is a custom amount value. */
  protected readonly _customOptionSelected = computed(
    () => this._selectedOption() === this._customOptionValue,
  );

  /** Unique value that is used to identify the custom amount radio item. */
  protected readonly _customOptionValue = Symbol('amount-field-custom-value');

  /** `true` if the user can provide a custom value. */
  protected readonly _showCustomOption = signal<boolean>(false);

  ngOnInit() {
    this._showCustomOption.set(!!this.field.attrs?.show_amount_text_input);

    // This flag tells the component to update
    const showPerInterval = this.getShowPerInterval();

    // This Observable keeps the donation amount in sync with
    // the available items. This might be needed as the
    // previously selected proposal might no longer be
    // available, so the amount will be unset.
    this.items$
      .pipe(skip(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((filteredItems) => {
        this.syncFromSelectedProposal(filteredItems);
      });

    if (showPerInterval) {
      // If the "per interval" options is active, the amount should
      // change according to the selected interval
      this._updateFromIntervalChange()
        .pipe(
          // Also take the latest from amountChange so that we can
          // correctly emit a new amount change
          withLatestFrom(
            this.amountChange.pipe(
              startWith<AmountChange>(new AmountChange(this.value, false)),
            ),
          ),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe(([newAmount, amountChange]) => {
          this.syncFromIntervalChange(newAmount, amountChange);
        });
    }

    // This `Observable` should keep the custom value in
    // sync with changes to the amount. This is necessary
    // as the items are emitted and the amount might change
    // programmatically via `writeValue`.
    this.amountChange
      .pipe(
        // In case that the "per interval" option is active, changes
        // to the interval will trigger an amountChange AND items
        // In this case we want to delay the event to the next iteration
        // to make sure that both changes have arrived correctly
        showPerInterval ? debounceTime(0) : (obs) => obs,
        // Emit with initial value
        startWith<AmountChange>(new AmountChange(this.value, false)),
        withLatestFrom(this.items$),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(([e, items]) => {
        this.syncCustomValue(e, items);
      });
  }

  /**
   * Sets the model value. Implemented as part of `ControlValueAccessor`.
   *
   * @param newValue
   */
  writeValue(newValue: unknown) {
    this._value.set(AmountFieldValueFromUnknown(newValue));
    this._selectedOption.set(this._value()?.toNumber() ?? null);
    this.emitAmountChange(false);
  }

  protected _onProposalSelected(value: unknown, proposal: AmountProposalItem) {
    this._customValue.set(null);
    this.value = AmountFieldValueFromUnknown(value);
    this.emitAmountChange(true, proposal);
    this._onTouched();
  }

  protected _onCustomAmountSelected() {
    if (this._customOptionSelected()) {
      return;
    }

    this._selectedOption.set(this._customOptionValue);
    this._setValueFromCustomValueChange();
  }

  protected _onCustomAmountInputClick(e: MouseEvent) {
    e.stopPropagation();
    this._onCustomAmountSelected();
  }

  protected _onCustomValueChange(value: Option<Decimal.Decimal>) {
    this._customValue.set(value);
    this._setValueFromCustomValueChange();
  }

  private _setValueFromCustomValueChange() {
    this.value = this._customValue();
    this.emitAmountChange(true);
  }

  /**
   * Emits the `amountChange` event.
   *
   * @param isUserInput `true` if the change was a result of a user interaction
   * @param proposal Selected amount proposal. `Undefined` if no proposal was selected.
   */
  private emitAmountChange(
    isUserInput: boolean,
    proposal?: AmountProposalItem,
  ) {
    this.amountChange.emit(new AmountChange(this.value, isUserInput, proposal));
  }

  /**
   * Updates `value` based on selected proposal and available items.
   *
   * @param items Available items
   */
  private syncFromSelectedProposal(items: AmountProposalItem[]) {
    const amount = this.value;
    if (!optionHasValue(amount) || !this.proposalSelected()) {
      // Nothing to do, if we're not dealing with a selected proposal
      return;
    }

    if (!items.some(findWithValue(amount))) {
      // Reset bc previously selected item is no longer available
      this.value = null;
      this.selectedOption = null;
      this.emitAmountChange(false);
    }
  }

  /**
   * Synchronizes the input values on changes to the amount by changing
   * the payment interval.
   *
   * @param newAmount New value of the field
   * @param amountChange The latest amount change event
   */
  private syncFromIntervalChange(
    newAmount: Decimal.Decimal,
    amountChange: AmountChange,
  ) {
    // First update internal value and emit change
    this._value.set(newAmount);

    if (amountChange.proposal) {
      // When a proposal was selected before, update selected option
      this._selectedOption.set(newAmount.toNumber());
    } else {
      // Otherwise update custom value
      this._customValue.set(newAmount);
    }

    this.emitAmountChange(amountChange.isUserInput, amountChange.proposal);
    // Then also propagate changes to the form
    this._onChange(this.value);
  }

  /**
   * Updates `customValue` on value change.
   *
   * @param e Change event
   * @param items Available items
   */
  private syncCustomValue(e: AmountChange, items: AmountProposalItem[]) {
    if (e.amount === null) {
      this._customValue.set(null);
    } else if (e.isUserInput) {
      // Set to null, if a proposal was selected
      this._customValue.set(e.proposal ? null : e.amount);
    } else {
      // Set to null, if the value is in the provided items
      const selectedProposal = items.some(findWithValue(e.amount));
      if (selectedProposal) {
        this._customValue.set(null);
        this._selectedOption.set(e.amount.toNumber());
      } else {
        this._customValue.set(e.amount);
      }
    }

    if (this._customValue() !== null) {
      this._selectedOption.set(this._customOptionValue);
    }
  }

  /**
   * @returns
   * An `Observable` that emits if the interval changed from
   * one recurring to another recurring, with the amount transformed
   * to the new interval.
   */
  private _updateFromIntervalChange() {
    return this.getInterval$().pipe(
      map(([from, to]) =>
        matchOption({
          onSome: (value: Decimal.Decimal) =>
            this.donationAmountService.transform(value, from, to),
        })(this._value()),
      ),
      // No need to update if the resulting amount is null
      filter(isNotNull),
    );
  }
}
